import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Share2, BarChart3, Users } from "lucide-react";
import { api } from "@/lib/api";
import "@/components/HomePolls.css";

export default function PollResultsPage() {
  const { slug } = useParams();
  const [poll, setPoll] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [photo, setPhoto] = useState(null);

  useEffect(() => {
    api.get(`/polls/by-slug/${slug}`, { params: { results: true } })
      .then((r) => setPoll(r.data))
      .catch((e) => { if (e.response?.status === 404) setNotFound(true); });
    const iv = setInterval(() => {
      api.get(`/polls/by-slug/${slug}`, { params: { results: true } })
        .then((r) => setPoll(r.data))
        .catch(() => {});
    }, 15000);
    return () => clearInterval(iv);
  }, [slug]);

  if (notFound) {
    return (
      <div className="poll-page light">
        <div className="poll-page-inner text-center py-20">
          <h1 className="text-4xl font-headline mb-3">Encuesta no encontrada</h1>
          <Link to="/" className="text-[#ec4899]">← Ir al inicio</Link>
        </div>
      </div>
    );
  }
  if (!poll) return null;

  const theme = poll.results_theme || "light";
  const bgType = poll.results_bg_type || "solid";
  const bgStyle = bgType === "image" && poll.results_bg_image
    ? { backgroundImage: `url(${poll.results_bg_image})`, backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed" }
    : bgType === "transparent"
    ? { background: "transparent" }
    : { background: poll.results_bg_color || (theme === "dark" ? "#0f172a" : "#fafafa") };

  const options = poll.options || [];
  const total = poll.total_votes || 0;
  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: poll.title, url }); return; } catch (_) { /* noop */ }
    }
    await navigator.clipboard.writeText(url);
    alert("Enlace copiado");
  };

  return (
    <div className={`poll-page ${theme}`} style={bgStyle} data-testid="poll-results-page">
      <div className="poll-page-inner">
        <Link to="/" className="inline-flex items-center gap-1 text-sm font-bold mb-6 opacity-70 hover:opacity-100" data-testid="poll-back">
          <ArrowLeft size={14} /> Volver
        </Link>

        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest opacity-80">
            <BarChart3 size={13} /> RESULTADOS EN VIVO
          </span>
          <button onClick={share} className="text-xs font-bold uppercase tracking-widest opacity-70 hover:opacity-100 flex items-center gap-1" data-testid="poll-share">
            <Share2 size={12} /> Compartir
          </button>
        </div>

        <h1 className="font-headline uppercase text-4xl md:text-6xl leading-tight mb-2" style={{ letterSpacing: "0.01em" }}>{poll.title}</h1>
        {poll.question && <p className="text-lg md:text-xl mb-6 opacity-80 leading-snug">{poll.question}</p>}

        {poll.image && (
          <div className="aspect-[16/9] mb-8 rounded-xl overflow-hidden max-w-2xl">
            <img src={poll.image} alt={poll.title} className="w-full h-full object-cover" />
          </div>
        )}

        <div className="flex items-center gap-2 mb-6 text-sm opacity-80">
          <Users size={16} /> <strong>{total}</strong> {total === 1 ? "voto" : "votos"} totales
          {poll.status === "closed" && <span className="ml-3 px-2 py-0.5 rounded-full bg-black/10 text-xs font-bold uppercase">CERRADA</span>}
          {poll.status === "open" && <span className="ml-3 px-2 py-0.5 rounded-full text-xs font-bold uppercase" style={{ background: "rgba(34,197,94,0.18)", color: "#16a34a" }}>ABIERTA</span>}
        </div>

        <ul className="poll-result-list" style={{ maxWidth: "720px" }}>
          {options.map((o, i) => (
            <li key={o.id} className={`poll-result-row ${i === 0 && (o.votes || 0) > 0 ? "winner" : ""}`} style={{ marginBottom: 22 }}>
              <div className="flex items-center gap-4 mb-1">
                {o.image && (
                  <img
                    src={o.image}
                    alt={o.text}
                    className="w-20 h-20 rounded-lg object-cover flex-shrink-0 cursor-pointer transition hover:scale-105"
                    onClick={() => setPhoto({ src: o.image, name: o.text })}
                    data-testid={`poll-result-photo-${o.id}`}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-center gap-2">
                    <span className="font-bold text-lg">{o.text}</span>
                    <span className="font-headline text-3xl" style={{ color: "var(--accent, #ec4899)" }}>{(o.pct || 0).toFixed(1)}%</span>
                  </div>
                  <div className="poll-result-bar mt-2" style={{ height: 14 }}>
                    <div className="poll-result-fill" style={{ width: `${o.pct || 0}%` }} />
                  </div>
                  <span className="text-xs opacity-70 mt-1 inline-block">{o.votes || 0} {(o.votes || 0) === 1 ? "voto" : "votos"}</span>
                </div>
              </div>
            </li>
          ))}
        </ul>

        {photo && (
          <div className="poll-lightbox" onClick={() => setPhoto(null)}>
            <button className="poll-lightbox-close" onClick={() => setPhoto(null)}>✕</button>
            <div className="poll-lightbox-inner" onClick={(e) => e.stopPropagation()}>
              {photo.src && <img src={photo.src} alt={photo.name} className="poll-lightbox-img" />}
              <p className="poll-lightbox-name">{photo.name}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
